// app/shared/utils/server-health.ts
/**
 * Server health check utility to prevent request spam when backend is down
 */

type HealthStatus = 'healthy' | 'unhealthy' | 'checking';

class ServerHealthMonitor {
  private status: HealthStatus = 'healthy';
  private lastCheckTime = 0;
  private readonly CHECK_INTERVAL = 30000; // 30 seconds between checks when unhealthy
  private readonly TIMEOUT = 5000; // 5 second timeout for health checks
  private checkPromise: Promise<boolean> | null = null;

  /**
   * Check if the server is healthy
   * Returns cached status if checked recently, otherwise performs a new check
   */
  async isHealthy(): Promise<boolean> {
    const now = Date.now();
    
    // If we're currently checking, wait for that check to complete
    if (this.checkPromise) {
      return this.checkPromise;
    }

    // If status is healthy, assume it still is (optimistic)
    if (this.status === 'healthy') {
      return true;
    }

    // If unhealthy and not enough time has passed, return cached status
    if (this.status === 'unhealthy' && now - this.lastCheckTime < this.CHECK_INTERVAL) {
      return false;
    }

    // Perform a new health check
    return this.performHealthCheck();
  }

  /**
   * Perform an actual health check against the server
   */
  private async performHealthCheck(): Promise<boolean> {
    this.status = 'checking';
    this.lastCheckTime = Date.now();

    this.checkPromise = (async () => {
      try {
        // Try to fetch a simple endpoint with a timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.TIMEOUT);

        const response = await fetch('/api/health', {
          method: 'GET',
          signal: controller.signal,
          cache: 'no-store',
          // Prevent Next.js error overlay from triggering
          headers: {
            'x-health-check': 'true',
          },
        }).catch((err) => {
          // Silently catch network errors to prevent Next.js error reporting
          clearTimeout(timeoutId);
          throw err;
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          console.log('✅ Server health check: HEALTHY');
          this.status = 'healthy';
          return true;
        } else {
          console.warn('⚠️ Server health check: UNHEALTHY (bad status)', response.status);
          this.status = 'unhealthy';
          return false;
        }
      } catch (error: any) {
        // Silently mark as unhealthy without logging to prevent spam
        this.status = 'unhealthy';
        return false;
      } finally {
        this.checkPromise = null;
      }
    })();

    return this.checkPromise;
  }

  /**
   * Mark the server as unhealthy (called when a request fails)
   */
  markUnhealthy(): void {
    console.log('🔴 Server marked as UNHEALTHY');
    this.status = 'unhealthy';
    this.lastCheckTime = Date.now();
  }

  /**
   * Mark the server as healthy (called when a request succeeds)
   */
  markHealthy(): void {
    if (this.status !== 'healthy') {
      console.log('🟢 Server marked as HEALTHY');
    }
    this.status = 'healthy';
  }

  /**
   * Get current status without performing a check
   */
  getStatus(): HealthStatus {
    return this.status;
  }

  /**
   * Reset the health monitor
   */
  reset(): void {
    this.status = 'healthy';
    this.lastCheckTime = 0;
    this.checkPromise = null;
  }
}

// Export singleton instance
export const serverHealthMonitor = new ServerHealthMonitor();

